import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import Container from 'components/container/Container';
import Plan from './components/plan/Plan';
import {getServices} from 'services/index';
import useSubscribe from 'hooks/useSubscribe';
import useStaticContent from 'hooks/useStaticContent';
import useAuth from 'hooks/useAuth';
import Logout from './components/logout/Logout';
import Success from './components/success/Success';
import Failed from './components/failed/Failed';
import Manage from './components/manage/Manage';

const PlansPage = () => {
  const [active, setActive] = useState<string>();
  const {state, subscribe} = useSubscribe();
  const {plans, loading} = useStaticContent();
  const {authService} = getServices();
  const {user} = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get('status'));
  useEffect(() => {
    const newStatus = params.get('status');
    if (newStatus) {
      setStatus(newStatus);
    }
    if (newStatus === 'failed') {
      const timerId = setTimeout(() => {
        setStatus('');
      }, 5000);
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [params]);

  useEffect(() => {
    if (!active && plans.length) {
      setActive(plans[plans.length - 1].id);
    }
  }, [plans, active]);

  const onlyFreePlanAvailable =
    plans.length === 1 && plans[0].title.toLowerCase() === 'free';

  useEffect(() => {
    if (onlyFreePlanAvailable) {
      setStatus('success');
      window.location.hash = '?status=success';
    }
  }, [onlyFreePlanAvailable]);

  if (loading) {
    return (
      <Container>
        <p>Loading...</p>
      </Container>
    );
  }

  if (status === 'success') {
    return <Success />;
  }

  if (status === 'failed') {
    return <Failed />;
  }

  if (onlyFreePlanAvailable) {
    return (
      <Container>
        <p>Loading...</p>
      </Container>
    );
  }

  const isSubscribed = authService.profile?.subscription?.id;
  if (isSubscribed) {
    return (
      <Manage
        name={user?.name}
        plan={plans.find(
          (p) => p.id === authService.profile?.subscription?.planStripeId
        )}
      />
    );
  }

  return (
    <Container>
      <div className="w-full px-12">
        <label className="font-medium text-left text-brand-forest mb-2 inline-block">
          Step 2 of 4
        </label>
        <h1 className="text-3xl font-bold mb-4 text-brand-forest">
          Choose a plan that's right for you
        </h1>
      </div>
      <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-4 py-8">
        {plans?.map((plan) => {
          const isActive = active === plan.id;
          return (
            <Plan
              key={plan.id}
              {...plan}
              processing={state.isSubscribing}
              active={isActive}
              onPick={() => {
                if (isActive) {
                  if (!plan.price) {
                    setStatus('success');
                    window.location.hash = '?status=success';
                    return;
                  }
                  subscribe(plan);
                  return;
                }
                setActive(plan.id);
              }}
              current={
                plan.id === authService.profile?.subscription?.planStripeId
              }
              recommended={plan.period === 'year'}
            />
          );
        })}
      </div>
      <Logout className="rounded-xl" />
    </Container>
  );
};

export default PlansPage;
